import { useState } from 'react';

import type { FormAnswer, FormNotice, FormResponse } from '../fixtures/domain.js';
import { isAnswered } from '../data/forms.js';
import { Icon } from '../components/Icon.js';
import { plural } from '../util/format.js';
import { FormPreview } from './FormPreview.js';
import styles from './FormMessageCard.module.css';

/**
 * The form at the foot of the message that asked it — the employer's copy, so
 * nothing here answers anything. The questions fold open for re-reading;
 * once answered, they open onto the picks.
 */
export function FormInviteCard({
  notice,
  answers,
  link,
}: {
  notice: FormNotice;
  /** What came back, once it has — resolved by the thread from the response
   *  message the seal names. Absent while unanswered or out of the window. */
  answers?: FormAnswer[];
  /** Where the form still has a page the head goes to it; otherwise the card
   *  folds open onto the questions. */
  link?: { open: () => void };
}) {
  const [open, setOpen] = useState(false);
  const answered = isAnswered(notice);
  const head = (
    <>
      <span className={styles.icon}>
        <Icon name="listChecks" size={16} />
      </span>
      <span className={styles.headText}>
        <span className={styles.name}>{notice.name}</span>
        <span className={styles.meta}>
          {plural(notice.fields.length, 'question')}
          {' · '}
          {answered ? <span className={styles.answered}>Answered</span> : 'Waiting for an answer'}
        </span>
      </span>
    </>
  );

  if (link) {
    return (
      <div className={styles.card}>
        <button type="button" className={styles.head} onClick={link.open}>
          {head}
          <span className={styles.chevron}>
            <Icon name="chevronRight" size={16} />
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <button
        type="button"
        className={styles.head}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {head}
        <span className={open ? styles.chevronOpen : styles.chevron}>
          <Icon name="chevronDown" size={16} />
        </span>
      </button>
      {open && (
        <div className={styles.body}>
          <FormPreview fields={notice.fields} answers={answers} />
        </div>
      )}
    </div>
  );
}

/**
 * The answers, on the jobseeker's own message, drawn in place of its text —
 * the text stays on the message as the fallback for surfaces that do not draw
 * cards. Questions are read off the message that asked (resolved by the
 * thread); when that is outside the loaded window the body says where they are.
 */
export function FormAnswersCard({
  response,
  notice,
  fallback,
  onView,
}: {
  response: FormResponse;
  notice?: FormNotice;
  fallback: string;
  /** Where a Responses page can show the answers: the card is then its head
   *  and this one row. */
  onView?: () => void;
}) {
  return (
    <div className={styles.card}>
      <div className={styles.headStatic}>
        <span className={styles.icon}>
          <Icon name="listChecks" size={16} />
        </span>
        <span className={styles.headText}>
          <span className={styles.name}>{notice?.name ?? fallback}</span>
          <span className={styles.meta}>{plural(response.answers.length, 'answer')}</span>
        </span>
      </div>
      {onView ? (
        <button type="button" className={styles.view} onClick={onView}>
          View answers
          <Icon name="chevronRight" size={15} />
        </button>
      ) : (
        <div className={styles.body}>
          {notice ? (
            <FormPreview fields={notice.fields} answers={response.answers} pickedOnly />
          ) : (
            <p className={styles.orphan}>
              The questions are further up this conversation. Scroll up to read them with the
              answers.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
